import React from 'react';
import { iFloorMapVersion } from 'services/FloorMap/FloorMap.type';

export function formatVersionDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const btn: React.CSSProperties = {
  border: '1px solid rgba(17,24,39,0.2)',
  background: '#fff',
  color: 'rgba(17,24,39,0.85)',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const overlay: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 200,
  background: 'rgba(17,24,39,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const dialog: React.CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  width: 'min(640px, 100%)',
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  color: '#1a1a1a',
  fontSize: 12,
};

const badge: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 4,
  background: '#dcfce7',
  color: '#166534',
  fontSize: 10,
  fontWeight: 700,
  verticalAlign: 'middle',
};

const cell: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid #eee',
  textAlign: 'left',
  verticalAlign: 'middle',
};

interface HistoryModalProps {
  versions: iFloorMapVersion[];
  loading: boolean;
  error: string | null;
  busy: boolean;
  onPreview: (version: iFloorMapVersion) => void;
  onRestore: (version: iFloorMapVersion) => void;
  onClose: () => void;
}

export function FloorMapHistoryModal({
  versions,
  loading,
  error,
  busy,
  onPreview,
  onRestore,
  onClose,
}: HistoryModalProps): JSX.Element {
  return (
    <div
      style={overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-label="Historique de la carte" style={dialog}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700 }}>
            Historique de la carte
          </span>
          <button style={btn} onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '4px 16px 16px' }}>
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
              Chargement…
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: 16, color: '#b91c1c' }}>{error}</div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
              Aucune version enregistrée.
            </div>
          )}

          {!loading && versions.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#666', fontSize: 11 }}>
                  <th style={{ ...cell, fontWeight: 600 }}>Version</th>
                  <th style={{ ...cell, fontWeight: 600 }}>Date</th>
                  <th style={{ ...cell, fontWeight: 600 }}>Créée par</th>
                  <th style={{ ...cell, fontWeight: 600 }} />
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id}>
                    <td style={cell}>
                      <b>v{v.version}</b>
                      {v.is_current && <span style={badge}>Active</span>}
                      {v.restored_from_version != null && (
                        <div style={{ color: '#888', fontSize: 11 }}>
                          Restaurée depuis v{v.restored_from_version}
                        </div>
                      )}
                    </td>
                    <td style={cell}>{formatVersionDate(v.created_at)}</td>
                    <td style={cell}>{v.creator_name ?? '—'}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <button
                        style={btn}
                        disabled={busy}
                        onClick={() => onPreview(v)}
                      >
                        Voir
                      </button>
                      {!v.is_current && (
                        <button
                          style={{ ...btn, marginLeft: 6 }}
                          disabled={busy}
                          onClick={() => onRestore(v)}
                        >
                          Restaurer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

interface PreviewBannerProps {
  version: iFloorMapVersion;
  busy: boolean;
  onRestore: () => void;
  onExit: () => void;
}

export function FloorMapPreviewBanner({
  version,
  busy,
  onRestore,
  onExit,
}: PreviewBannerProps): JSX.Element {
  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 150,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
        padding: '8px 14px',
        background: '#fef3c7',
        borderBottom: '1px solid #f59e0b',
        color: '#78350f',
        fontSize: 12,
      }}
    >
      <span>
        👁 Aperçu de la version <b>v{version.version}</b> (
        {formatVersionDate(version.created_at)}) — lecture seule, rien
        n&apos;est enregistré.
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          style={{
            ...btn,
            background: '#1f4e9c',
            borderColor: '#1f4e9c',
            color: '#fff',
          }}
          disabled={busy}
          onClick={onRestore}
        >
          Restaurer cette version
        </button>
        <button style={btn} disabled={busy} onClick={onExit}>
          Quitter l&apos;aperçu
        </button>
      </div>
    </div>
  );
}
